import { type Location } from '@koala/sdk';
import { memo, type ReactNode } from 'react';
import { useTheme } from 'styled-components';
import { StyledConveyance, StyledConveyanceChip, StyledIcon, StyledInfoRow } from './styles';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { DeliveryIcon } from '@/components/uielements/icon/delivery';
import { PickupIcon } from '@/components/uielements/icon/pickup';
import { StorefrontIcon } from '@/components/uielements/icon/storefront';

type ConveyanceLabel = 'Curbside' | 'Delivery' | 'Drive Thru' | 'Pickup' | 'Dine In' | 'Drive In';

/**
 * The fulfillment methods a location supports, in display order. Six labels from eight flags:
 * Dine In is on when any of `supports_to_stay`, `supports_dine_in` or
 * `supports_dine_in_table_number` is on. Exported so the map popup can show the same labels
 * (TEAM-15027).
 */
export const getConveyanceLabels = (location: Location): ConveyanceLabel[] => {
  const conveyanceObject: Record<ConveyanceLabel, boolean | undefined> = {
    Curbside: location.supports_curbside,
    Delivery: location.supports_delivery,
    'Drive Thru': location.supports_drive_thru,
    Pickup: location.supports_pick_up,
    'Dine In':
      location.supports_to_stay ||
      location.supports_dine_in ||
      location.supports_dine_in_table_number,
    'Drive In': location.supports_drive_in,
  };

  return (Object.keys(conveyanceObject) as ConveyanceLabel[]).filter(
    (key) => conveyanceObject[key],
  );
};

/** Methods with a dedicated icon; every other method falls back to the storefront. */
const CONVEYANCE_ICONS: Partial<Record<ConveyanceLabel, ReactNode>> = {
  Pickup: <PickupIcon width="16" height="16" />,
  Delivery: <DeliveryIcon width="16" height="16" />,
};

/**
 * Online-ordering availability (TEAM-15026). Accepting orders: today's hours when there are any,
 * otherwise "Online ordering available". That fallback is deliberately not
 * `getOperatingHoursString`: `deriveTodaysHours` returns '' on a closed or split-hours day, and
 * for a store with business hours that helper's bare "Open" would claim a closed store is open.
 * Not accepting: the brand's `locations.location_inactive` string. A location whose custom CTA
 * attribute replaces the order buttons keeps today's behaviour (hours only, no inactive copy).
 * The map popup should use the same wording (TEAM-15027).
 */
export const getLocationAvailability = (location: Location, todaysHours: string): ReactNode => {
  if (location.provider_enabled) {
    return todaysHours || 'Online ordering available';
  }

  // Not accepting online orders, and no custom CTA attribute replaces the order buttons.
  if (!location.attributes?.some((attr) => attr.value && attr.label)) {
    return <StringAccessor accessor="locations.location_inactive" html={true} />;
  }

  return todaysHours || null;
};

interface CardInfoRowProps {
  icon: ReactNode;
  testId: string;
  children: ReactNode;
}

/** One icon-led info row. The icon is decorative: the text beside it says the same thing. */
export const CardInfoRow = ({ icon, testId, children }: CardInfoRowProps) => (
  <StyledInfoRow data-testid={testId}>
    <StyledIcon aria-hidden="true">{icon}</StyledIcon>
    <p>{children}</p>
  </StyledInfoRow>
);

interface Props {
  location: Location;
}

const _LocationConveyance = ({ location }: Props) => {
  const theme = useTheme();
  // Ensure support
  if (!theme.location_card.show_conveyance_types) {
    return null;
  }

  const labels = getConveyanceLabels(location);

  if (!labels.length) {
    return null;
  }

  return (
    <StyledConveyance data-testid="location-card-conveyance">
      {labels.map((label) => (
        <StyledConveyanceChip key={label}>
          {/* Decorative: the label beside it says the same thing. */}
          <StyledIcon aria-hidden="true">
            {CONVEYANCE_ICONS[label] ?? <StorefrontIcon />}
          </StyledIcon>
          {label}
        </StyledConveyanceChip>
      ))}
    </StyledConveyance>
  );
};

export const LocationConveyance = memo(_LocationConveyance);
